import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "../mock";

const links = [
  { to: "/", label: "Home" },
  { to: "/rooms", label: "Suites" },
  { to: "/experience", label: "Experience" },
  { to: "/activities", label: "Activities" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const transparent = onHome && !scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-700 ${
        transparent
          ? "bg-transparent text-[#f5f0e6]"
          : "bg-[#f5f0e6]/90 backdrop-blur-md text-[#1f2a24] border-b border-[#e3dac6]"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-baseline gap-2 group">
            <span className="font-italiana text-[26px] md:text-[32px] tracking-[0.18em] uppercase">
              Casa&nbsp;Elio
            </span>
            <span className="hidden md:block text-[10px] uppercase tracking-[0.3em] opacity-70">
              San Juan del Sur
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.25em] link-underline ${
                    isActive ? "opacity-100" : "opacity-80 hover:opacity-100"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2 border ${
                transparent
                  ? "border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24]"
                  : "border-[#1f2a24]/40 hover:bg-[#1f2a24] hover:text-[#f5f0e6]"
              } transition-colors duration-500`}
            >
              Booking.com
            </a>
            <a
              href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                "Hi Casa Elio! I'd like to book a stay in San Juan del Sur."
              )}`}
              target="_blank"
              rel="noreferrer"
              className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2 ${
                transparent
                  ? "bg-[#f5f0e6] text-[#1f2a24]"
                  : "bg-[#2f4438] text-[#f5f0e6] hover:bg-[#1f2a24]"
              } transition-colors duration-500`}
            >
              Book Now
            </a>
          </div>

          <button
            className="md:hidden p-2 -mr-2"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden bg-[#f5f0e6] text-[#1f2a24] transition-[max-height,opacity] duration-500 ease-in-out ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4 border-t border-[#e3dac6]">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="text-sm uppercase tracking-[0.25em]"
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-3">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-center text-[11px] uppercase tracking-[0.25em] px-4 py-3 border border-[#1f2a24]/40"
            >
              Booking.com
            </a>
            <a
              href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                "Hi Casa Elio! I'd like to book a stay."
              )}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-center text-[11px] uppercase tracking-[0.25em] px-4 py-3 bg-[#2f4438] text-[#f5f0e6]"
            >
              Book Now
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
